import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, X } from "lucide-react";
import { api, dateShort } from "../lib/api";

const empty = { name: "", phone: "", email: "", address: "", notes: "" };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api(`/admin/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then(setCustomers)
      .catch((e) => toast.error(e.message));
  };

  useEffect(() => {
    load();
     
  }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.email) delete body.email;
      await api("/admin/customers", { method: "POST", body });
      toast.success("Cliente creado");
      setForm(empty);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="customers-view" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-[#A66E38] mb-3">
            CRM
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-black text-[#F5F0E6]">
            Clientes
          </h1>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="new-customer-btn"
          className="flex items-center gap-2 bg-[#A66E38] hover:bg-[#B88655] text-[#F5F0E6] uppercase tracking-widest text-xs py-3 px-6 transition-colors"
        >
          <Plus size={16} /> Nuevo cliente
        </button>
      </div>

      <div className="relative max-w-md">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7D71]"
          size={16}
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="customer-search"
          placeholder="Buscar por nombre, teléfono o correo"
          className="w-full bg-[#12100E] border border-white/10 text-[#F5F0E6] pl-10 pr-4 py-3 outline-none focus:border-[#C89B3C] transition-colors"
        />
      </div>

      <div
        data-testid="customer-list"
        className="bg-[#12100E] border border-white/5 overflow-hidden"
      >
        {customers.length === 0 ? (
          <p className="p-8 text-sm text-[#8A7D71]">
            Aún no hay clientes. Crea el primero.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#8A7D71] border-b border-white/5">
                <th className="py-3 px-4">Nombre</th>
                <th className="py-3 px-4">Teléfono</th>
                <th className="py-3 px-4">Correo</th>
                <th className="py-3 px-4">Dirección</th>
                <th className="py-3 px-4">Alta</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  data-testid={`customer-row-${c.id}`}
                >
                  <td className="py-3 px-4">
                    <Link
                      to={`/admin/customers/${c.id}`}
                      className="text-[#F5F0E6] hover:text-[#C89B3C] transition-colors"
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td className="py-3 px-4 text-[#B8A99A]">{c.phone || "—"}</td>
                  <td className="py-3 px-4 text-[#B8A99A]">{c.email || "—"}</td>
                  <td className="py-3 px-4 text-[#B8A99A]">
                    {c.address || "—"}
                  </td>
                  <td className="py-3 px-4 text-[#8A7D71]">
                    {dateShort(c.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="new-customer-modal"
        >
          <form
            onSubmit={submit}
            className="w-full max-w-lg bg-[#12100E] border border-[#A66E38]/30 p-8 relative"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-[#8A7D71] hover:text-[#F5F0E6]"
              data-testid="close-customer-modal"
            >
              <X size={20} />
            </button>
            <h2 className="font-serif text-2xl text-[#F5F0E6] mb-6">
              Nuevo cliente
            </h2>
            <div className="space-y-4">
              {[
                { k: "name", label: "Nombre *", type: "text", required: true },
                { k: "phone", label: "Teléfono", type: "tel" },
                { k: "email", label: "Correo", type: "email" },
                { k: "address", label: "Dirección", type: "text" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                    {f.label}
                  </label>
                  <input
                    type={f.type}
                    required={f.required}
                    value={form[f.k]}
                    onChange={(e) =>
                      setForm({ ...form, [f.k]: e.target.value })
                    }
                    data-testid={`customer-input-${f.k}`}
                    className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] focus:border-[#C89B3C] outline-none py-2.5 px-4"
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                  Notas
                </label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="customer-input-notes"
                  className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] focus:border-[#C89B3C] outline-none py-2.5 px-4 resize-none"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-customer"
              className="mt-6 w-full bg-[#A66E38] hover:bg-[#B88655] disabled:opacity-50 text-[#F5F0E6] uppercase tracking-widest text-xs py-3 transition-colors"
            >
              {saving ? "Guardando..." : "Guardar cliente"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
